import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { ApiV1Service, AssessmentResultSnapshot } from 'api';
import { firstValueFrom } from 'rxjs';

@Component({
  templateUrl: 'declared-compliant.component.html',
  styleUrl: 'declared-compliant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslateModule],
})
export class DeclaredCompliantComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly apiV1Service = inject(ApiV1Service);
  private readonly translateService = inject(TranslateService);

  protected readonly assessmentResult = signal<AssessmentResultSnapshot | undefined>(undefined);
  protected readonly isLoading = signal(true);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly shareMessage = signal<string | undefined>(undefined);
  protected readonly isSharePanelOpen = signal(false);
  protected readonly isSharedView = signal(false);
  protected readonly shareUrl = computed(() => {
    const shareKey = this.assessmentResult()?.shareKey;
    return shareKey ? `${window.location.origin}/share/${shareKey}` : undefined;
  });

  constructor() {
    const shareKey = this.route.snapshot.paramMap.get('shareKey');
    if (shareKey) {
      this.isSharedView.set(true);
      void this.loadAssessmentResultByShareKey(shareKey);
      return;
    }

    const revisionIdParam =
      this.route.snapshot.paramMap.get('revisionId') ??
      this.route.parent?.snapshot.paramMap.get('revisionId');
    const revisionId = Number(revisionIdParam);

    if (!Number.isInteger(revisionId)) {
      this.isLoading.set(false);
      this.errorMessage.set(
        this.translateService.instant('declaredCompliant.errors.unableToResolveDeclaration'),
      );
      return;
    }

    void this.loadAssessmentResultByRevision(revisionId);
  }

  private async loadAssessmentResultByRevision(revisionId: number): Promise<void> {
    this.isLoading.set(true);

    try {
      this.assessmentResult.set(
        await firstValueFrom(this.apiV1Service.getAssessmentResultForRevision(revisionId)),
      );
      this.errorMessage.set(undefined);
    } catch {
      this.errorMessage.set(
        this.translateService.instant('declaredCompliant.errors.loadDocumentFailed'),
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadAssessmentResultByShareKey(shareKey: string): Promise<void> {
    this.isLoading.set(true);

    try {
      this.assessmentResult.set(await firstValueFrom(this.apiV1Service.getByShareKey(shareKey)));
      this.errorMessage.set(undefined);
    } catch {
      this.errorMessage.set(
        this.translateService.instant('declaredCompliant.errors.loadSharedDocumentFailed'),
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  protected toggleSharePanel(): void {
    this.isSharePanelOpen.update((isOpen) => !isOpen);
    this.shareMessage.set(undefined);
    this.errorMessage.set(undefined);
  }

  protected closeSharePanel(): void {
    this.isSharePanelOpen.set(false);
  }

  protected openShareUrl(shareUrl: string): void {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }

  protected async copyShareUrl(shareUrl: string): Promise<void> {
    this.shareMessage.set(undefined);
    this.errorMessage.set(undefined);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        this.shareMessage.set(this.translateService.instant('declaredCompliant.share.copySuccess'));
        this.errorMessage.set(undefined);
        return;
      } catch {
        this.shareMessage.set(undefined);
        this.errorMessage.set(
          this.translateService.instant('declaredCompliant.share.copyFailed', { shareUrl }),
        );
        return;
      }
    }

    this.errorMessage.set(
      this.translateService.instant('declaredCompliant.share.notSupported', { shareUrl }),
    );
  }
}
